<template>
    <div>
        <div class="viewfinder" :class="{ viewfinder__horizontal: ratio >= 1, viewfinder__vertical: ratio < 1 }" ref="viewfinder">
            <video class="viewfinder__source" ref="camera" :id="id" autoplay playsinline></video>

            <div class="viewfinder__patch" :style="patchStyle.top"></div>
            <div class="viewfinder__patch" :style="patchStyle.left"></div>
            <div class="viewfinder__patch" :style="patchStyle.right"></div>
            <div class="viewfinder__patch" :style="patchStyle.bottom"></div>
            <div class="viewfinder__point" :style="pointStyle['top-left']"></div>
            <div class="viewfinder__point" :style="pointStyle['top-right']"></div>
            <div class="viewfinder__point" :style="pointStyle['bottom-left']"></div>
            <div class="viewfinder__point" :style="pointStyle['bottom-right']"></div>

            <div class="viewfinder__frame" style="position: absolute" :style="frameStyle" @click="saveImage">
                <div class="viewfinder__icons" style="position: absolute; bottom: 0; left: 0; width: 100%; height: 50px; display: flex; flex-direction: row; justify-content: flex-end">
                    <span v-if="!this.isInFocus" style="background: white; padding: 15px; font-weight: bold; display: inline-block">Non a fuoco</span>
                    <span v-if="!this.isCleanFromHotspots" style="background: white; padding: 15px; font-weight: bold; display: inline-block">Hotspots</span>
                    <span v-if="!this.hasBorders" style="background: white; padding: 15px; font-weight: bold; display: inline-block">Mancano i bordi</span>
                </div>
            </div>

            <iframe class="viewfinder__iframe" ref="iframe" allowtransparency="true"></iframe>

            <button class="btn btn-primary" :disabled="buttonsDisabled" style="position: absolute; top: 50%; right: 0; transform: translateY(-50%)" type="button" @click.prevent="saveImage">GO</button>
            <button class="btn btn-primary" :disabled="buttonsDisabled" style="position: absolute; top: 50%; left: 0; transform: translateY(-50%)" type="button" @click.prevent="saveImage">GO</button>
        </div>
        <canvas ref="canvas" id="NMCanvas" style="display: none"></canvas>
    </div>
</template>

<script>

import DetectRTC from 'detectrtc';
import $config from '@/services/config';

const installJs = (id, url) => {
    return new Promise((resolve, reject) => {
        if (document.getElementById(id)) {
            return resolve();
        }

        let js = document.createElement('script');
        js.id = id;
        js.src = url;
        js.onreadystatechange = js.onload = () => {
            if (!js.readyState || /loaded|compvare/.test(js.readyState)) {
                setTimeout(() => {
                    resolve();
                }, 500);
            }
        };

        const fjs = document.getElementsByTagName('script')[0];
        fjs.parentNode.appendChild(js);
    });
};

export default {
    name: 'take-rtc-image',
    props: {
        maskRatio: {
            type: Number,
            default: 1.54,
        },
    },
    data() {
        return {
            video: null,
            canvas: null,

            origW: null,
            origH: null,
            w: null,
            h: null,

            scanTimeout: null,

            isInFocus: false,
            isCleanFromHotspots: true,
            hasBorders: false,

            onFocusThreshold: $config.get('onFocusThreshold', 100),
            hotspotThreshold: $config.get('hotspotThreshold', 100),
            triggerThreshold: $config.get('triggerThreshold', 5),
        };
    },
    computed: {
        id() {
            return 'video-camera-' + this._uid;
        },
        ratio() {
            if (!Number.isInteger(this.origW) || !Number.isInteger(this.origH) || !this.origH) {
                return null;
            }

            return this.origW / this.origH;
        },
        viewfinderCoords() {
            let maskH = 0;
            let maskW = 0;

            if (this.maskRatio < this.ratio) {
                // maschera più verticale dell'immagine

                const cutPointY = Math.floor(this.h * .1);

                // 1. trovo la dimensione in verticale
                maskH = Math.floor(this.h - (2 * cutPointY));

                // 2. calcolo la larghezza della maschera
                maskW = Math.min(
                    Math.floor(this.maskRatio * maskH),
                    Math.floor(this.w * .8)
                );

                // 3. riaggiusto l'altezza se necessario
                maskH = Math.floor(maskW / this.maskRatio);

            } else {
                // maschera più orizzontale o uguale all'immagine

                const cutPointX = Math.floor(this.w * .1);

                // 1. trovo la dimensione in orizzontale
                maskW = Math.floor(this.w - (2 * cutPointX));

                // 2. calcolo l'altezza della maschera
                maskH = Math.min(
                    Math.floor(maskW / this.maskRatio),
                    Math.floor(this.h * .8)
                );

                // 3. riaggiusto la larghezza se necessario
                maskW = Math.floor(maskH * this.maskRatio);
            }

            // 4. centro la maschera
            const maskY = Math.floor((this.h - maskH) / 2);
            const maskX = Math.floor((this.w - maskW) / 2);

            return {
                x1: maskX,
                y1: maskY,
                x2: maskX + maskW,
                y2: maskY + maskH,
            };
        },
        viewfinderSize() {
            return this.w ? 50 : 1;
        },

        frameStyle() {
            const coords = this.viewfinderCoords;

            return {
                top: coords.y1 + 'px',
                left: coords.x1 + 'px',
                width: (coords.x2 - coords.x1 + 1) + 'px',
                height: (coords.y2 - coords.y1 + 1) + 'px',
            };
        },
        patchStyle() {
            const coords = this.viewfinderCoords;

            return {
                top: {
                    top: 0,
                    left: 0,
                    width: this.w + 'px',
                    height: (coords.y1 - 0) + 'px',
                },
                bottom: {
                    top: (coords.y2 + 1) + 'px',
                    left: 0,
                    width: this.w + 'px',
                    height: (this.h - coords.y2) + 'px',
                },
                left: {
                    top: (coords.y1) + 'px',
                    height: (coords.y2 - coords.y1 + 1) + 'px',
                    left: 0,
                    width: coords.x1 + 'px',
                },
                right: {
                    top: (coords.y1) + 'px',
                    height: (coords.y2 - coords.y1 + 1) + 'px',
                    left: (coords.x2 + 1) + 'px',
                    width: (this.w - coords.x2) + 'px',
                },
            };
        },

        pointStyle() {
            const coords = this.viewfinderCoords;

            return {
                'top-left': {
                    top: coords.y1 + 'px',
                    left: coords.x1 + 'px',
                    width: this.viewfinderSize + 'px',
                    height: this.viewfinderSize + 'px',
                    'border-bottom': 0,
                    'border-right': 0,
                },
                'top-right': {
                    top: coords.y1 + 'px',
                    left: (coords.x2 - this.viewfinderSize) + 'px',
                    width: this.viewfinderSize + 'px',
                    height: this.viewfinderSize + 'px',
                    'border-bottom': 0,
                    'border-left': 0,
                },
                'bottom-left': {
                    top: (coords.y2 - this.viewfinderSize) + 'px',
                    left: coords.x1 + 'px',
                    width: this.viewfinderSize + 'px',
                    height: this.viewfinderSize + 'px',
                    'border-top': 0,
                    'border-right': 0,
                },
                'bottom-right': {
                    top: (coords.y2 - this.viewfinderSize) + 'px',
                    left: (coords.x2 - this.viewfinderSize) + 'px',
                    width: this.viewfinderSize + 'px',
                    height: this.viewfinderSize + 'px',
                    'border-top': 0,
                    'border-left': 0,
                },
            };
        },

        originalCutCoords() {
            const viewfinderCoords = this.viewfinderCoords;

            return {
                x1: Math.floor(Math.floor(viewfinderCoords.x1 * this.origW) / this.w),
                x2: Math.floor(Math.floor(viewfinderCoords.x2 * this.origW) / this.w),
                y1: Math.floor(Math.floor(viewfinderCoords.y1 * this.origH) / this.h),
                y2: Math.floor(Math.floor(viewfinderCoords.y2 * this.origH) / this.h),
            };
        },
        buttonsDisabled() {
            return !this.isInFocus || !this.isCleanFromHotspots || !this.hasBorders;
        },
    },
    methods: {
        clear() {
            clearTimeout(this.scanTimeout);
            window.document.removeEventListener("Acknowledge", this.acknowledgeEventHandler);
            window.document.removeEventListener("FocusStatusChange", this.focusStatusChangeEventHandler);
            window.document.removeEventListener("BorderDetectionStatusChange", this.borderDetectionStatusChangeHandler);
            window.document.removeEventListener("HotspotStatusChange", this.hotspotStatusChangeEventHandler);
            if (window.stream) {
                window.stream.getTracks().forEach(track => track.stop());
            }
            this.video = null;
            this.canvas = null;
            window.viewFinder.setProcessing(false);
        },
        saveImage() {
            if (this.buttonsDisabled) {
                //return;
            }

            const cutCoords = this.originalCutCoords;
            //const viewfinderCoords = this.viewfinderCoords;

            this.canvas.width  = cutCoords.x2 - cutCoords.x1;
            this.canvas.height = cutCoords.y2 - cutCoords.y1;
            const ctx = this.canvas.getContext('2d');
            ctx.fillStyle = "rgb(0,0,255)";
            ctx.strokeStyle = "rgb(0,0,255)";
            ctx.drawImage(this.video, cutCoords.x1, cutCoords.y1, cutCoords.x2 - cutCoords.x1, cutCoords.y2 - cutCoords.y1, 0, 0, cutCoords.x2 - cutCoords.x1, cutCoords.y2 - cutCoords.y1);

            const dataURL = this.canvas.toDataURL("image/png");
            this.$emit('setImage', dataURL);
            /*
            alert(JSON.stringify({
                videoWidth: this.origW,
                videoHeight: this.origH,
                srcX: cutCoords.x1,
                srcY: cutCoords.y1,
                srcWidth: cutCoords.x2 - cutCoords.x1,
                srcHeight: cutCoords.y2 - cutCoords.y1,

                canvasWidth: this.canvas.width,
                canvasHeight: this.canvas.height,
                destX: 0,
                destY: 0,
                destWidth: cutCoords.x2 - cutCoords.x1,
                destHeight: cutCoords.y2 - cutCoords.y1,

                maskRatio: this.maskRatio,
                ratio: this.ratio,
                videoInfo: window.stream.getVideoTracks()[0].getSettings(),
            }, null, 4));
            */
        },
        updateSizes() {
            if (!this.$refs.viewfinder) {
                return;
            }

            if (window.stream) {
                this.origW = window.stream.getVideoTracks()[0].getSettings().width;
                this.origH = window.stream.getVideoTracks()[0].getSettings().height;
            }

            const iframe = this.$refs.iframe;
            this.w = iframe.clientWidth;
            this.h = iframe.clientHeight;

            if (window.viewFinder && this.id && this.origW && this.origH) {
                const cutCoords = this.originalCutCoords;
                //const width  = 100;
                const width  = cutCoords.x2 - cutCoords.x1;
                // const width = this.canvas.width;
                //const height = 100;
                const height = cutCoords.y2 - cutCoords.y1;
                // const height = this.canvas.height;

                console.log('updatesizes', this.origW, cutCoords.x1, width, this.origH, cutCoords.y1, height);

//                window.viewFinder.setWidth(this.canvas.width);
//                window.viewFinder.setHeight(this.canvas.height);
                window.viewFinder.setWidth(this.origW);
                window.viewFinder.setHeight(this.origH);
                window.viewFinder.setROI({
                    //x: 0,
                    x: cutCoords.x1,
                    //y: 0,
                    y: cutCoords.y1,
                    width,
                    height,
                });
                window.viewFinder.setVideo(this.id);
            }
        },

        initViewFinder() {
            const cutCoords = this.originalCutCoords;
            //const width  = 100;
            const width  = cutCoords.x2 - cutCoords.x1;
            // const width = this.canvas.width;
            //const height = 100;
            const height = cutCoords.y2 - cutCoords.y1;
            // const height = this.canvas.height;

            window.viewFinder.setDebug(false);
            window.viewFinder.setProcessing(false);
            window.viewFinder.setFocusThreshold(this.onFocusThreshold);
            window.viewFinder.setTriggerThreshold(this.triggerThreshold);
            window.viewFinder.setHotspotThreshold(this.hotspotThreshold);

//                window.viewFinder.setWidth(this.canvas.width);
//                window.viewFinder.setHeight(this.canvas.height);
            window.viewFinder.setWidth(this.origW);
            window.viewFinder.setHeight(this.origH);
            window.viewFinder.setROI({
                //x: 0,
                x: cutCoords.x1,
                //y: 0,
                y: cutCoords.y1,
                width,
                height,
            });

            window.viewFinder.setVideo(this.id);

            console.log('init', this.origW, cutCoords.x1, width, this.origH, cutCoords.y1, height);

            this.scanTimeout = setTimeout(this.scan, 1000);
        },
        scan() {
            const begin = Date.now();

            if (!this.canvas || !this.video) {
                return;
            }

            if (window.viewFinder.getProcessing()) {
                window.viewFinder.processFrame();
            } else {
                let delay = 1000/1 - (Date.now() - begin);
                this.scanTimeout = setTimeout(this.scan, delay);
            }
        },
        acknowledgeEventHandler(e) {
            //console.log('acknowledge', e);
            this.scanTimeout = setTimeout(this.scan, 0);
        },
        focusStatusChangeEventHandler(e) {
            this.isInFocus = e.detail.inFocus;
        },
        hotspotStatusChangeEventHandler(e) {
            this.isCleanFromHotspots = !e.detail.hasHotspot;
        },
        borderDetectionStatusChangeHandler(e) {
            this.hasBorders = e.detail.value;
        },
    },
    mounted() {
        DetectRTC.load(() => {
            if (!DetectRTC.isWebRTCSupported) {
                alert('Please use Chrome, Firefox, iOS 11, Android 5 or higher, Safari 11 or higher');
                return;
            }

            if (!DetectRTC.hasWebcam) {
                alert('Please install an external webcam device.');
                return;
            }

            if (window.stream) {
                window.stream.getTracks().forEach(track => track.stop());
            }

            this.video  = this.$refs.camera;
            this.canvas = this.$refs.canvas;

            navigator.mediaDevices
                .getUserMedia({
                    audio: false,
                    video: {
                        width: { min: 640, ideal: 1280, max: 1920 },
                        height: { min: 480, ideal: 720, max: 1080 },
                        facingMode: 'environment',
                        aspectRatio: { exact: 1.77 },
                    },
                })
                .then(stream => {
                    window.stream = stream;
                    this.video.srcObject = stream;

                    return stream;
                })
                .then(stream => {
                    this.origW = stream.getVideoTracks()[0].getSettings().width;
                    this.origH = stream.getVideoTracks()[0].getSettings().height;

                    this.updateSizes();
                    window.addEventListener('resize', this.updateSizes);
                    this.$refs.iframe.contentWindow.addEventListener('resize', this.updateSizes);
                })
                .then(() => installJs('viewfinder', 'js/viewfinder.js'))
                .then(() => {
                    this.initViewFinder();
                })
                .then(() => {
                    window.document.addEventListener("FocusStatusChange", this.focusStatusChangeEventHandler, false);
                    window.document.addEventListener("HotspotStatusChange", this.hotspotStatusChangeEventHandler, false);
                    window.document.addEventListener("BorderDetectionStatusChange", this.borderDetectionStatusChangeHandler, false);
                    window.document.addEventListener("Acknowledge", this.acknowledgeEventHandler, false);
                })
                .catch(error => {
                    console.log(error);

                    //https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
                    if ('PermissionDeniedError' === error) {
                        alert("Permission denied. Please refresh and give permission.");
                    }
                })
            ;
        });
    },
    beforeDestroy() {
        this.clear();
    },
    destroyed() {
        window.removeEventListener('resize', this.updateSizes);
    },
};

</script>

<style lang="scss">

.viewfinder {
    display: inline-block;
    position: relative;
    overflow: hidden;

    &__source {
        display: block;
    }

    &__horizontal .viewfinder__source {
        width: 100%;
        max-width: 100vw;
    }

    &__vertical .viewfinder__source {
        height: 100%;
        max-height: 100vh;
    }

    &__patch {
        display: block;
        position: absolute;
        background: rgba(255, 255, 255, .6);
    }

    &__point {
        display: block;
        position: absolute;
        width: 2px;
        height: 2px;
    }

    &__iframe {
        border: none;
        display: block;
        width: 100%;
        background: transparent;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: -50;
    }
}

</style>
